/**
 * The IFRAME's half of the postMessage bridge.
 *
 * THE ONE THING THIS FILE EXISTS FOR: `hostOrigin` is learned from
 * `event.origin`, once, on the first `init` we accept — and never from a field
 * anybody put in a message. The browser sets `event.origin` and no sender can
 * change it, which makes it the only trustworthy statement about who is framing
 * us. That value then becomes the `targetOrigin` of every message we send back
 * and the expected origin of every message we accept.
 *
 * It is also what goes to the API as `pageOrigin` for the `allowed_origins`
 * check. A forwarded field would have made that check circular: the page would be
 * telling us which page it is, and we would be asking the server whether the page
 * that page claims to be is allowed.
 *
 * PINNED, not tracked. Once an origin is accepted it does not change for the life
 * of the frame. A second `init` from somewhere else is ignored rather than
 * treated as a re-parent: a frame does not change embedders, so a second one is
 * either a bug or an attempt.
 */
import { isFromPeer, PROTOCOL_ID, type HostPalette, type WidgetMessage } from '../loader/protocol';
import { readHostPalette } from '../theme/host-palette';
import { toThemePreference, type ThemePreference } from '../theme/theme';

import { readCallNumber, readJwt, type IdentityOutcome } from './identity';

export interface HostHandlers {
  /** Fires once, when the handshake completes and `hostOrigin` becomes known. */
  readonly onInit: (init: {
    theme: ThemePreference;
    locale: string | null;
    origin: string;
    /** The host page's colours, narrowed; null from a loader that predates them. */
    palette: HostPalette | null;
    /**
     * The site called `identify` before the handshake (ADR-0249): the first
     * bootstrap should already be the player's. `n` is null from a loader that
     * sends a JWT without a call number — the outcome then has nowhere to go.
     */
    identity: { readonly jwt: string; readonly n: number | null } | null;
  }) => void;
  /** The site's player signed in (ADR-0249). Answer with `done(n, …)`. */
  readonly onIdentify: (jwt: string, n: number) => void;
  /** The site's player signed out (ADR-0249). Answer with `done(n, …)`. */
  readonly onLogout: (n: number) => void;
  /** The panel was shown or hidden by the launcher on the host page. */
  readonly onVisibility: (open: boolean) => void;
  /** The host page's own dark-mode toggle moved. */
  readonly onTheme: (theme: ThemePreference) => void;
}

export interface HostLink {
  /** null until the handshake completes, and for a frame nobody is framing. */
  readonly origin: string | null;
  /** Booted, with the brand's launcher colour if it has one. */
  ready(accent: string | null): void;
  /** The launcher's colour, before `ready` — the site's own accent (ADR-0248). */
  accent(colour: string): void;
  unread(count: number): void;
  /** The translated accessible name for the launcher on the host page. */
  label(text: string): void;
  /** The visitor closed the panel from inside — its own ✕, or Escape. */
  requestClose(): void;
  /** The outcome of `identify` / `logout` number `n`. */
  done(n: number, outcome: IdentityOutcome): void;
  dispose(): void;
}

/**
 * A widget opened directly at `https://widget.example.com/?k=…` — which is what
 * `pnpm dev` gives you — has no host. It must still render: developing the panel
 * should not require a second server and a customer page. Everything that would
 * be sent upward becomes a no-op, and `origin` stays null.
 */
function isFramed(): boolean {
  return window.parent !== window;
}

export function createHostLink(handlers: HostHandlers): HostLink {
  let hostOrigin: string | null = null;

  function post(message: WidgetMessage): void {
    if (hostOrigin === null) {
      return;
    }
    // An explicit targetOrigin on every send, always. `'*'` here would hand the
    // visitor's unread count — and, the day this protocol carries more, their
    // transcript — to whatever document happens to be framing us.
    window.parent.postMessage(message, hostOrigin);
  }

  const onMessage = (event: MessageEvent<unknown>): void => {
    // `window.parent` and not a stored reference: if this frame were somehow
    // re-parented, the comparison must be against the parent we have NOW.
    if (!isFromPeer(event, hostOrigin ?? event.origin, window.parent)) {
      return;
    }
    const message = event.data as {
      readonly t: string;
      readonly theme?: unknown;
      readonly locale?: unknown;
      readonly palette?: unknown;
      readonly jwt?: unknown;
      readonly n?: unknown;
    };

    if (message.t === 'init') {
      if (hostOrigin !== null) {
        // Already handshaken. A frame does not change embedders, so a second init
        // is a bug or an attempt; either way the pinned origin stands.
        return;
      }
      hostOrigin = event.origin;
      const initialJwt = readJwt(message.jwt);
      handlers.onInit({
        theme: toThemePreference(message.theme),
        locale: typeof message.locale === 'string' && message.locale !== '' ? message.locale : null,
        origin: event.origin,
        palette: readHostPalette(message.palette),
        identity: initialJwt === null ? null : { jwt: initialJwt, n: readCallNumber(message.n) },
      });
      return;
    }

    // Everything below requires a completed handshake. Without the pin, the
    // `hostOrigin ?? event.origin` above would accept any sender, so this guard is
    // what keeps the very first message the only one that can be from anywhere.
    if (hostOrigin === null) {
      return;
    }

    if (message.t === 'open') {
      handlers.onVisibility(true);
      return;
    }
    if (message.t === 'close') {
      handlers.onVisibility(false);
      return;
    }
    if (message.t === 'theme') {
      handlers.onTheme(toThemePreference(message.theme));
      return;
    }
    if (message.t === 'identify') {
      const n = readCallNumber(message.n);
      if (n === null) {
        return;
      }
      const jwt = readJwt(message.jwt);
      if (jwt === null) {
        // Answered rather than dropped: the site is waiting on a promise, and
        // silence would surface as a 15-second `unavailable` instead of the
        // truth, which is that this was not a token.
        post({ p: PROTOCOL_ID, t: 'done', n, ok: false, r: 'identity_invalid' });
        return;
      }
      handlers.onIdentify(jwt, n);
      return;
    }
    if (message.t === 'logout') {
      const n = readCallNumber(message.n);
      if (n !== null) {
        handlers.onLogout(n);
      }
    }
  };

  if (isFramed()) {
    window.addEventListener('message', onMessage);
  }

  return {
    get origin(): string | null {
      return hostOrigin;
    },
    ready: (accent) => {
      post({ p: PROTOCOL_ID, t: 'ready', accent });
    },
    accent: (colour) => {
      post({ p: PROTOCOL_ID, t: 'accent', accent: colour });
    },
    unread: (count) => {
      post({ p: PROTOCOL_ID, t: 'unread', count });
    },
    label: (text) => {
      post({ p: PROTOCOL_ID, t: 'label', text });
    },
    requestClose: () => {
      post({ p: PROTOCOL_ID, t: 'close' });
    },
    done: (n, outcome) => {
      post(
        outcome.ok
          ? { p: PROTOCOL_ID, t: 'done', n, ok: true }
          : { p: PROTOCOL_ID, t: 'done', n, ok: false, r: outcome.reason },
      );
    },
    dispose: () => {
      window.removeEventListener('message', onMessage);
    },
  };
}
